// AI-ASSISTED: generated with claude-code on 2026-08-18, reviewed by Yoann Hertienne
import useAsync from 'react-use/lib/useAsync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

const PROJECT_ANNOTATION = 'dev.azure.com/project';
const BUILD_DEFINITION_ANNOTATION = 'dev.azure.com/build-definition';
const ORG_ANNOTATION = 'dev.azure.com/org';
const DEFAULT_ORG = 'LuminusITDigital';

// Artefact published by the PR pipeline (see .pipelines/templates/quality-gate.yaml).
const ARTIFACT_NAME = 'npm-audit';
const ARTIFACT_FILE = 'npm-audit-results.json';
const API_VERSION = '7.1';

export type SeverityCounts = {
  critical: number;
  high: number;
  moderate: number;
  low: number;
};

// Rendered order: highest-impact first. Colour is supplementary — every chip
// also carries the count and severity name in its label + accessible name so
// the state is legible without colour (WCAG 2.1 AA).
export const SEVERITIES: ReadonlyArray<{
  key: keyof SeverityCounts;
  label: string;
  color: 'error' | 'warning' | 'info' | 'default';
}> = [
  { key: 'critical', label: 'Critical', color: 'error' },
  { key: 'high', label: 'High', color: 'warning' },
  { key: 'moderate', label: 'Moderate', color: 'info' },
  { key: 'low', label: 'Low', color: 'default' },
];

const emptyCounts = (): SeverityCounts => ({ critical: 0, high: 0, moderate: 0, low: 0 });

/**
 * Read the four rendered severity counts from a standard `npm audit --json`
 * summary object (`metadata.vulnerabilities`). Missing or non-numeric fields
 * degrade to `0`.
 */
export const parseAuditCounts = (payload: unknown): SeverityCounts => {
  const empty = emptyCounts();
  if (!payload || typeof payload !== 'object') return empty;
  const meta = (payload as Record<string, unknown>).metadata;
  if (!meta || typeof meta !== 'object') return empty;
  const v = (meta as Record<string, unknown>).vulnerabilities;
  if (!v || typeof v !== 'object') return empty;
  const counts = v as Record<string, unknown>;
  const num = (x: unknown): number =>
    typeof x === 'number' && Number.isFinite(x) ? x : 0;
  return {
    critical: num(counts.critical),
    high: num(counts.high),
    moderate: num(counts.moderate),
    low: num(counts.low),
  };
};

/**
 * Count severities from Yarn Berry's `yarn npm audit --json` NDJSON stream:
 * one advisory per line as `{ value, children: { Severity } }`. Unlike npm's
 * summary object there is no `metadata` block, so we tally the per-line
 * `children.Severity` values. Blank or unparseable lines are skipped.
 */
export const parseNdjsonCounts = (raw: string): SeverityCounts => {
  const counts = emptyCounts();
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      const entry = JSON.parse(trimmed) as { children?: { Severity?: unknown } };
      const severity = entry?.children?.Severity;
      if (typeof severity === 'string') {
        const key = severity.toLowerCase();
        if (key === 'critical' || key === 'high' || key === 'moderate' || key === 'low') {
          counts[key] += 1;
        }
      }
    } catch {
      // Non-JSON line (e.g. a trailing yarn banner) — ignore.
    }
  }
  return counts;
};

/**
 * Parse the raw artefact file into severity counts. Supports BOTH producers:
 * the standard `npm audit --json` summary object (`metadata.vulnerabilities`)
 * and Yarn Berry's `yarn npm audit --json` NDJSON stream. The shape is chosen
 * by structure, so the card works regardless of the consumer repo's package
 * manager.
 */
export const parseAuditPayload = (raw: string): SeverityCounts => {
  const text = raw.trim();
  if (!text) return emptyCounts();
  try {
    const obj = JSON.parse(text) as Record<string, unknown>;
    if (obj && typeof obj === 'object' && 'metadata' in obj) {
      return parseAuditCounts(obj);
    }
  } catch {
    // Not a single JSON object → fall through to the NDJSON reader.
  }
  return parseNdjsonCounts(text);
};

/**
 * `dev.azure.com/build-definition` may list several pipelines (comma-separated).
 * Only the PR pipeline publishes the `npm-audit` artefact, so prefer an entry
 * that looks like a pull-request pipeline; otherwise fall back to the first.
 */
export const pickAuditDefinition = (annotationValue: string): string => {
  const names = annotationValue
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (names.length === 0) return annotationValue.trim();
  return names.find(n => /pull.?request|^pr$/i.test(n)) ?? names[0];
};

/**
 * Azure DevOps build-artefact resources reference their file container as
 * `#/{containerId}/{artifactName}`. Extract the numeric container id used to
 * download an individual file via the Containers API.
 */
export const parseContainerId = (data: unknown): number | undefined => {
  if (typeof data !== 'string') return undefined;
  const match = data.match(/#\/(\d+)\//);
  return match ? Number(match[1]) : undefined;
};

type AuditResult = {
  counts: SeverityCounts;
  pipelineUrl: string;
};

const NpmAuditCardBody = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const annotations = entity.metadata.annotations ?? {};
  const buildDefinition = annotations[BUILD_DEFINITION_ANNOTATION];
  const project = annotations[PROJECT_ANNOTATION];
  const org = annotations[ORG_ANNOTATION] ?? DEFAULT_ORG;

  const { value, loading, error } = useAsync(async (): Promise<AuditResult | undefined> => {
    if (!buildDefinition || !project) return undefined;
    const baseUrl = `${await discoveryApi.getBaseUrl('proxy')}/azure-devops`;
    const orgPath = `${baseUrl}/${encodeURIComponent(org)}`;
    const projectPath = `${orgPath}/${encodeURIComponent(project)}`;
    const definition = pickAuditDefinition(buildDefinition);

    const get = async (url: string) => {
      const response = await fetchApi.fetch(url);
      if (!response.ok) {
        throw new Error(`Azure DevOps request failed: ${response.status}`);
      }
      return response.json();
    };

    // 1. Resolve the PR pipeline name to a numeric definition id.
    const definitions = await get(
      `${projectPath}/_apis/build/definitions?name=${encodeURIComponent(
        definition,
      )}&api-version=${API_VERSION}`,
    );
    const definitionId = definitions?.value?.[0]?.id;
    if (!definitionId) return undefined;

    // 2. Latest successful, completed build for that definition.
    const builds = await get(
      `${projectPath}/_apis/build/builds?definitions=${definitionId}` +
        `&resultFilter=succeeded&statusFilter=completed&$top=1` +
        `&queryOrder=finishTimeDescending&api-version=${API_VERSION}`,
    );
    const build = builds?.value?.[0];
    if (!build) return undefined;
    const pipelineUrl: string =
      build._links?.web?.href ??
      `https://dev.azure.com/${encodeURIComponent(org)}/${encodeURIComponent(
        project,
      )}/_build?definitionName=${encodeURIComponent(definition)}`;

    // 3. Locate the `npm-audit` build artefact → its file container.
    // A 404 here means the latest successful build never published the
    // artefact (e.g. its pipeline predates the audit step) — that's "no data",
    // NOT a broken integration, so return undefined to render the calm empty
    // state rather than the TileBoundary degraded state (spec FR-008).
    const artifactResponse = await fetchApi.fetch(
      `${projectPath}/_apis/build/builds/${build.id}/artifacts` +
        `?artifactName=${encodeURIComponent(ARTIFACT_NAME)}&api-version=${API_VERSION}`,
    );
    if (artifactResponse.status === 404) return undefined;
    if (!artifactResponse.ok) {
      throw new Error(`Azure DevOps request failed: ${artifactResponse.status}`);
    }
    const artifact = await artifactResponse.json();
    const containerId = parseContainerId(artifact?.resource?.data);
    if (!containerId) return undefined;

    // 4. Download the artefact file via the `npm-audit` BACKEND plugin, not the
    // `/azure-devops` proxy. ADO stores container files gzip-compressed and
    // streams them with `Content-Encoding: gzip`; the built-in proxy forwards
    // the compressed bytes but drops that header, so the browser never
    // decompresses and we read gzip mojibake. Node/undici (backend) auto-
    // decompresses, so the route returns clean text — Yarn NDJSON or the npm
    // summary object, both handled by parseAuditPayload.
    const containerBase = await discoveryApi.getBaseUrl('npm-audit');
    const itemResponse = await fetchApi.fetch(
      `${containerBase}/container?org=${encodeURIComponent(org)}` +
        `&containerId=${containerId}` +
        `&itemPath=${encodeURIComponent(`${ARTIFACT_NAME}/${ARTIFACT_FILE}`)}`,
    );
    if (!itemResponse.ok) {
      throw new Error(`Azure DevOps request failed: ${itemResponse.status}`);
    }
    return { counts: parseAuditPayload(await itemResponse.text()), pipelineUrl };
  }, [buildDefinition, project, org]);

  if (!buildDefinition) return null;
  if (error) throw error;

  const result = value;
  const counts = result?.counts;
  const healthy = counts ? counts.critical === 0 && counts.high === 0 : false;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">npm audit</Typography>
        <Typography variant="body2" color="text.secondary">
          Dependency vulnerabilities from the latest PR pipeline run
        </Typography>
        <Box sx={{ pt: 1 }}>
          {loading && (
            <CircularProgress size={20} aria-label="Loading npm audit results" />
          )}
          {!loading && !counts && (
            <Typography variant="body2" color="text.secondary">
              No npm audit artefact found for the latest pipeline run.
            </Typography>
          )}
          {!loading && counts && (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {SEVERITIES.map(({ key, label, color }) => (
                  <Chip
                    key={key}
                    size="small"
                    color={color}
                    variant={counts[key] > 0 ? 'filled' : 'outlined'}
                    label={`${label}: ${counts[key]}`}
                    aria-label={`${counts[key]} ${label} vulnerabilities`}
                  />
                ))}
              </Stack>
              {healthy && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CheckIcon fontSize="small" />}
                  label="No critical or high vulnerabilities"
                />
              )}
            </Stack>
          )}
        </Box>
      </CardContent>
      {result && (
        <CardActions>
          <Button
            variant="outlined"
            size="small"
            href={result.pipelineUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="View pipeline run that produced these npm audit results"
          >
            View pipeline
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

export const NpmAuditCard = () => (
  <TileBoundary tool="npm audit">
    <NpmAuditCardBody />
  </TileBoundary>
);
